import { useEffect, useRef } from 'react';

import { CookieCategory } from '../types';
import { useConsent } from './useConsent';

export interface ConsentScriptOptions {
  /** id univoco del tag <script> (dedup). */
  id: string;
  src: string;
  /** Inizializzazione dopo il load (es. gtag config). */
  onLoad?: () => void;
  /** Teardown alla revoca (es. disabilita GA, pulisci i cookie). */
  onRevoke?: () => void;
}

/**
 * Carica uno script di terze parti SOLO quando la categoria è concessa, e lo
 * rimuove/segnala la revoca quando il consenso viene tolto.
 *
 * Le callback sono tenute in un ref così l'effect dipende solo da
 * [granted, id, src]: callback inline non causano teardown/revoca a ogni render.
 */
export function useConsentScript(category: CookieCategory, opts: ConsentScriptOptions): void {
  const granted = useConsent(category);
  const { id, src } = opts;
  const cbRef = useRef(opts);
  cbRef.current = opts;

  useEffect(() => {
    if (!granted) return;

    const existing = document.getElementById(id);
    if (existing) {
      cbRef.current.onLoad?.();
      return;
    }

    const script = document.createElement('script');
    script.id = id;
    script.src = src;
    script.async = true;
    script.onload = () => cbRef.current.onLoad?.();
    document.head.appendChild(script);

    return () => {
      // Revoca: molti SDK non si "scaricano" davvero → segnala opt-out e rimuovi il nodo.
      cbRef.current.onRevoke?.();
      document.getElementById(id)?.remove();
    };
  }, [granted, id, src]);
}
